/**
 * Handles submission of the form.
 */

/**
 * WordPress dependencies.
 */
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';

/**
 * External dependencies.
 */
import { v4 as uuidv4 } from 'uuid';

export default function handleSubmitDonationForm(payment_method = 'vipps', formURL, formData, appID, setFormData) {
	const url = new URL(formURL);
	const baseURL = url.protocol + '//' + url.hostname;

	setFormData({ loading: true, message: '' });

	const errorMessage = __('Sorry, there was an error with the donation. Please try again later.', 'dekode-profundo-forms');

	const {
		frequency,
		amount,
		anonymous,
		name,
		address,
		postcode,
		city,
		mobile,
		email,
		commentSingle,
		commentRecurring,
		tax_deduction,
		personal_id,
	} = formData;

	switch (payment_method) {
		case 'nets':

			apiFetch({
				path: '/dekode-profundo-form/v1/donation/nets',
				method: 'POST',
				data: {
					url: formURL,
					frequency,
					amount,
					anonym: anonymous ? 1 : 0,
					navn: name,
					address,
					postcode,
					city,
					mobile,
					email,
					commentSingle: 'single' === frequency ? commentSingle : '',
					commentRecurring: 'recurring' === frequency ? commentRecurring: '',
					tax_deduction,
					personal_id,
					fbq_eventid: '',
				},
			})
			.then((response) => {
				if ( response.redirect ) {
					const donationData = {
						'datetime': new Date().toISOString(),
						'uuid': uuidv4(),
						'recurring': formData['frequency'],
						'payment_method': payment_method,
						'value': formData['amount'],
					}

					localStorage.setItem('profundo_donation_data', JSON.stringify(donationData));

					analyticsEvent(formData, payment_method, donationData);

					/**
					 * Modify URLs from response before redirect.
					 * 
					 * Some redirect URLs in Profundo's response are relative,
					 * so we're using the form url to add the necessary hostname.
					 */
					let redirectUrl = response.redirect;

					if (0 === response.redirect.indexOf('/')) {
						redirectUrl = `${url.protocol}//${url.hostname}${redirectUrl}`;
					}

					window.location.href = redirectUrl;
				} else {
					setFormData({ loading: false, message: errorMessage });
				}
			})
			.catch((err) => {
				console.error(err);
				setFormData({ loading: false, message: errorMessage });
			});

			break;
		case 'avtalegiro':

			apiFetch({
				path: '/dekode-profundo-form/v1/donation/avtalegiro',
				method: 'POST',
				data: {
					url: formURL,
					frequency,
					amount,
					name,
					address,
					postcode,
					city,
					mobile,
					email,
					commentSingle,
					commentRecurring,
					tax_deduction,
					personal_id,
					fbq_eventid: '',
				},
			})
			.then(( response ) => {
				if ( response.redirect ) {
					const donationData = {
						'datetime': new Date().toISOString(),
						'uuid': uuidv4(),
						'recurring': formData['frequency'],
						'payment_method': payment_method,
						'value': formData['amount'],
					}

					localStorage.setItem('profundo_donation_data', JSON.stringify(donationData));

					analyticsEvent(formData, payment_method, donationData);

					/**
					 * Modify URLs from response before redirect.
					 * 
					 * Some redirect URLs in Profundo's response are relative,
					 * so we're using the form url to add the necessary hostname etc.
					 */
					let redirectUrl = response.redirect;
					
					if (0 === response.redirect.indexOf('/avtalegiro/signing')) {
						/**
						 * New avtalegiro signup.
						 * 
						 * Redirect response from Profundo is '/avtalegiro/signing'
						 * (followed by some parameters) and should redirect to
						 * <form host>/<response redirect>
						 */
						redirectUrl = `${url.protocol}//${url.hostname}${redirectUrl}`;
					} else if ('../avtalegirofrafor' === response.redirect) {
						/**
						 * User already has an existing avtalegiro.
						 * 
						 * Redirect response from Profundo is '../avtalegirofrafor',
						 * and should redirect to <form host>/<form path>/avtalegirofrafor
						 */
						// Prepend full URL from form, including form slug.
						redirectUrl = `${url}/avtalegirofrafor`;
					}

					window.location.href = redirectUrl;
				} else {
					setFormData({ loading: false, message: errorMessage });
				}
			})
			.catch((err) => {
				console.error(err);
				setFormData({ loading: false, message: errorMessage });
			});

			break;
		case 'vipps':
			const vippsType = 'single' === frequency ? 'gave' : 'avtale';
			const vippsURL = baseURL + `/vipps/${vippsType}?app=${appID}&amount=${amount}`;

			apiFetch({
				path: '/dekode-profundo-form/v1/donation/vipps',
				method: 'POST',
				data: {
					url: vippsURL,
				},
			})
			.then(( response ) => {
				if ( response.redirect ) {
					const donationData = {
						'datetime': new Date().toISOString(),
						'uuid': uuidv4(),
						'recurring': formData['frequency'],
						'payment_method': payment_method,
						'value': formData['amount'],
					}

					localStorage.setItem('profundo_donation_data', JSON.stringify(donationData));

					analyticsEvent(formData, payment_method, donationData);
					window.location.href = response.redirect;
				} else {
					setFormData({ loading: false, message: errorMessage });
				}
			})
			.catch((err) => {
				console.error(err);
				setFormData({ loading: false, message: errorMessage });
			});

			break;
		default:
			console.error('Unknown payment method: ' + payment_method);
	}
}

const analyticsEvent = (formData, payment_method, donationData) => {
	if ( ! window.dataLayer ) return;

	const newDataLayer = {
		'event': 'donation',
		'uuid': donationData['uuid'],
		'recurring': 'single' === formData['frequency'] ? 0 : 1, // 0 == once / 1 == recurring
		'payment_method': payment_method,
		'value': formData['amount'],
	}

	window.dataLayer.push( newDataLayer );
}